import { isPosthogReady, posthog } from './posthog';
import { pushGoogleTagEvent } from './google-tag';

/**
 * Closed event taxonomy. Adding a new event means adding a name here so
 * we don't end up with 'Started Session' / 'session-started' / 'sessionStart'
 * scattered across the code.
 *
 * Event names are lowercase + underscores so they line up with GA4's
 * recommended naming convention and reach the Google Tag workspace
 * unmodified — same string used to wire triggers in the workspace UI.
 */
export type EventName =
  | 'session_start'
  | 'mic_granted'
  | 'mic_denied'
  | 'first_word_latency'
  | 'conversation_start'
  | 'conversation_end'
  | 'limit_hit'
  | 'waitlist_signup'
  | 'waitlist_signup_failed'
  | 'page_error'
  | 'tab_hidden'
  | 'tab_visible'
  | 'network_offline'
  | 'network_online'
  | 'session_aborted_unmount'
  | 'mode_changed'
  | 'conversation_resumed';

export type EventProps = Record<string, string | number | boolean | null | undefined>;

/**
 * Fan one event call out to both PostHog and the Google Tag dataLayer.
 * Each destination is independently gated:
 *   - PostHog only when initialised (consent + key both present).
 *   - Google Tag dataLayer is always pushable client-side; gtag.js
 *     itself is only injected on consent, but the push is queued in
 *     `window.dataLayer` regardless — replayed if/when gtag.js loads,
 *     dropped on the floor otherwise. The Google Tag workspace at
 *     tagmanager.google.com is where each event gets routed to its
 *     destination (GA4, Meta Pixel, TikTok Pixel, ...).
 *
 * Server-side calls are no-ops in both branches.
 */
export function track(name: EventName, props: EventProps = {}): void {
  if (isPosthogReady()) {
    posthog.capture(name, props);
  }
  pushGoogleTagEvent(name, props);
}
